import { fetchProductReviewHelpfulNum } from '@/api/product';
import { useEffect, useMemo, useState } from 'react';
import { IReviewsItem } from '@/api/types/product';

/**
 * 根据reviewId合并两个列表数据
 * @param oldList 原始列表数据
 * @param newList 新的列表数据，用于更新原始列表中对应id的项
 * @returns 合并后的新数组，保持oldList的顺序，但用newList中相同id的数据更新对应项
 */
function mergeListByReviewId<T extends { id?: string | number }>(oldList: T[], newList: T[]): T[] {
  if (!oldList?.length) return [];
  if (!newList?.length) return oldList;
  // 创建以id为键的Map以便快速查找
  const partialReviewMap: Record<string, T> = {};
  newList.forEach((item) => {
    partialReviewMap[item.id] = item;
  });
  // 遍历旧列表，将新列表中的数据合并到旧列表中相同id的项上
  return oldList.map((item) => {
    return { ...item, ...partialReviewMap[item.id] };
  });
}

/**
 * 自定义Hook，用于获取商品评价列表中每条评论的"like"计数数据
 * @param list - 商品评价列表数据
 * @param limit - 限制请求数量，可选参数
 * @returns 包含"like"计数的部分评价数据列表
 */
export function useReviewListHelpful(list: IReviewsItem[], limit?: number) {
  const [partialReviewList, setPartialReviewList] = useState<Array<Partial<IReviewsItem>>>([]);

  // 仅首次，更新处理服务端缓存中商品评论的点赞数据
  useEffect(() => {
    if (!list?.length) return;
    const limitList = limit > 0 ? list.slice(0, limit) : list;
    const review_ids = limitList.map((item) => item.id.toString());
    fetchProductReviewHelpfulNum({ review_ids }).then((res) => {
      if (res?.data?.list?.length) {
        setPartialReviewList(res.data.list);
      }
    });
  }, []);

  return partialReviewList;
}

/**
 * 自定义Hook，用于合并原始评价列表和部分更新的评价数据
 * @param reviewList 原始评价列表数据
 * @param partialReviewList 部分更新的评价数据（如点赞数等）
 * @returns 合并后的评价列表数据
 */
export function useMergedReviewList(
  reviewList: IReviewsItem[],
  partialReviewList: Array<Partial<IReviewsItem>>,
) {
  return useMemo(() => {
    return mergeListByReviewId(reviewList, partialReviewList);
  }, [partialReviewList, reviewList]);
}
